<template>
  <div
    class="gallery-widget pb-mobile-responsive"
    :class="[
      `mobile-layout-${widget.mobileLayout || 'grid'}`,
      { 'carousel-fade-edges': widget.mobileLayout === 'carousel' && widget.carouselFadeEdges },
      widgetClasses,
    ]"
    :style="widgetRootStyle"
  >
    <div
      v-if="widget.images && widget.images.length > 0"
      class="pb-gallery-grid"
      :class="mobileLayoutClass"
      :style="{ ...galleryStyles, gap: widget.spacing || '1rem' }"
    >
      <!-- Carousel wrapper for carousel mode -->
      <div
        v-if="widget.mobileLayout === 'carousel'"
        class="gallery-carousel-track"
        :style="{
          gap: widget.spacing || '1rem',
          animationDuration: `${carouselSpeedSeconds}s`,
        }"
      >
        <!-- First set of images -->
        <div
          v-for="(image, index) in widget.images"
          :key="`first-${index}`"
          :class="[
            'gallery-item',
            {
              'gallery-card-link': image.link,
              'card-style': widget.styleMode === 'cards',
              'with-glow': widget.enableGradientGlow && image.link,
            },
          ]"
          :style="[itemStyles, getCarouselItemStyle(index)]"
          @click="handleGalleryItemClick(image, index)"
        >
          <div class="image-wrapper" :style="getImageWrapperStyle()">
            <img
              :src="getImageUrl(image.url, image)"
              :alt="image.alt || `Gallery image ${index + 1}`"
              class="gallery-image"
              loading="lazy"
              @error="handleImageError"
            />
          </div>

          <!-- Title and Caption -->
          <div
            v-if="((widget.styleMode === 'cards' || widget.showTitles) && image.title) || image.caption"
            class="image-content"
          >
            <div v-if="image.title && (widget.styleMode === 'cards' || widget.showTitles)" class="image-title">
              <div
                class="title-with-icons"
                :style="{ cursor: image.link ? 'pointer' : 'default' }"
                @click.stop="image.link && openExternalLink(image.link)"
              >
                <div class="title-section">
                  {{ image.title }}
                  <i v-if="image.link" class="bi bi-link-45deg link-icon"></i>
                </div>
                <span v-if="image.icons && image.icons.length > 0" class="icons-container">
                  <img
                    v-for="(icon, idx) in image.icons"
                    :key="idx"
                    :src="getIconUrl(icon.path)"
                    :alt="icon.alt || `icon-${idx}`"
                    class="title-icon"
                    loading="lazy"
                  />
                </span>
              </div>
            </div>
            <div v-if="image.caption" class="image-caption">
              {{ image.caption }}
            </div>
          </div>
        </div>

        <!-- Duplicate set for seamless loop -->
        <div
          v-for="(image, index) in widget.images"
          :key="`second-${index}`"
          :class="[
            'gallery-item',
            {
              'gallery-card-link': image.link,
              'card-style': widget.styleMode === 'cards',
              'with-glow': widget.enableGradientGlow && image.link,
            },
          ]"
          :style="[itemStyles, getCarouselItemStyle(index)]"
          @click="handleGalleryItemClick(image, index)"
        >
          <div class="image-wrapper" :style="getImageWrapperStyle()">
            <img
              :src="getImageUrl(image.url, image)"
              :alt="image.alt || `Gallery image ${index + 1}`"
              class="gallery-image"
              loading="lazy"
              @error="handleImageError"
            />
          </div>

          <!-- Title and Caption -->
          <div
            v-if="((widget.styleMode === 'cards' || widget.showTitles) && image.title) || image.caption"
            class="image-content"
          >
            <div v-if="image.title && (widget.styleMode === 'cards' || widget.showTitles)" class="image-title">
              <div
                class="title-with-icons"
                :style="{ cursor: image.link ? 'pointer' : 'default' }"
                @click.stop="image.link && openExternalLink(image.link)"
              >
                <div class="title-section">
                  {{ image.title }}
                  <i v-if="image.link" class="bi bi-link-45deg link-icon"></i>
                </div>
                <span v-if="image.icons && image.icons.length > 0" class="icons-container">
                  <img
                    v-for="(icon, idx) in image.icons"
                    :key="idx"
                    :src="getIconUrl(icon.path)"
                    :alt="icon.alt || `icon-${idx}`"
                    class="title-icon"
                    loading="lazy"
                  />
                </span>
              </div>
            </div>
            <div v-if="image.caption" class="image-caption">
              {{ image.caption }}
            </div>
          </div>
        </div>
      </div>

      <!-- Normal rendering for other modes -->
      <div
        v-else
        v-for="(image, index) in widget.images"
        :key="index"
        :class="[
          'gallery-item',
          {
            'gallery-card-link': image.link,
            'card-style': widget.styleMode === 'cards',
            'with-glow': widget.enableGradientGlow && image.link,
          },
        ]"
        :style="[itemStyles, getStackCardStyle(index), getImageItemStyle(index)]"
        @click="handleGalleryItemClick(image, index)"
      >
        <div class="image-wrapper" :style="getImageWrapperStyle()">
          <img
            :src="getImageUrl(image.url, image)"
            :alt="image.alt || `Gallery image ${index + 1}`"
            class="gallery-image"
            loading="lazy"
            @error="handleImageError"
          />
        </div>

        <!-- Title and Caption -->
        <div
          v-if="((widget.styleMode === 'cards' || widget.showTitles) && image.title) || image.caption"
          class="image-content"
        >
          <div v-if="image.title && (widget.styleMode === 'cards' || widget.showTitles)" class="image-title">
            <div
              class="title-with-icons"
              :style="{ cursor: image.link ? 'pointer' : 'default' }"
              @click.stop="image.link && openExternalLink(image.link)"
            >
              <div class="title-section">
                {{ image.title }}
                <i v-if="image.link" class="bi bi-link-45deg link-icon"></i>
              </div>
              <span v-if="image.icons && image.icons.length > 0" class="icons-container">
                <img
                  v-for="(icon, idx) in image.icons"
                  :key="idx"
                  :src="getIconUrl(icon.path)"
                  :alt="icon.alt || `icon-${idx}`"
                  class="title-icon"
                  loading="lazy"
                />
              </span>
            </div>
          </div>
          <div v-if="image.caption" class="image-caption">
            {{ image.caption }}
          </div>
        </div>
      </div>
    </div>

    <!-- Empty gallery -->
    <div v-else class="empty-gallery text-muted text-center p-4">
      <i class="bi bi-images fs-2 mb-2"></i>
      <p class="mb-0">Δεν υπάρχουν εικόνες στη συλλογή</p>
    </div>

    <!-- Lightbox Modal (if enabled) -->
    <Teleport to="body">
      <div
        v-if="widget.lightbox && lightboxOpen"
        class="lightbox-modal"
        @click="closeLightbox"
        @keydown.esc="closeLightbox"
      >
        <button
          type="button"
          class="lightbox-close"
          aria-label="Κλείσιμο"
          @click.stop="closeLightbox"
        >
          <i class="bi bi-x-lg"></i>
        </button>

        <button
          v-if="widget.images.length > 1"
          type="button"
          class="lightbox-nav-btn lightbox-prev"
          aria-label="Προηγούμενη εικόνα"
          @click.stop="prevImage"
          :disabled="currentImageIndex === 0"
        >
          <i class="bi bi-chevron-left"></i>
        </button>

        <button
          v-if="widget.images.length > 1"
          type="button"
          class="lightbox-nav-btn lightbox-next"
          aria-label="Επόμενη εικόνα"
          @click.stop="nextImage"
          :disabled="currentImageIndex === widget.images.length - 1"
        >
          <i class="bi bi-chevron-right"></i>
        </button>

        <div class="lightbox-content" @click.stop>
          <img
            :src="getImageUrl(currentImage.url, currentImage)"
            :alt="currentImage.alt"
            class="lightbox-image"
          />

          <div
            v-if="widget.images.length > 1"
            ref="lightboxThumbnailsRef"
            class="lightbox-thumbnails"
          >
            <button
              v-for="(image, index) in widget.images"
              :key="index"
              type="button"
              class="lightbox-thumbnail"
              :class="{ active: index === currentImageIndex }"
              :aria-label="image.alt || `Εικόνα ${index + 1}`"
              :aria-current="index === currentImageIndex ? 'true' : undefined"
              @click.stop="goToImage(index)"
            >
              <img
                :src="getImageUrl(image.url, image)"
                :alt="image.alt || ''"
                loading="lazy"
              />
            </button>
          </div>

          <div v-if="currentImage.caption" class="lightbox-caption">
            {{ currentImage.caption }}
          </div>

          <div v-if="widget.images.length > 1" class="lightbox-counter">
            {{ currentImageIndex + 1 }} / {{ widget.images.length }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import type { GalleryWidget } from '@/types/pagebuilder'
import { useWidgetStyles } from '@/composables/useWidgetStyles'
import { assignResponsiveCssVar } from '@/utils/responsiveWidgetSize'

interface Props {
  widget: GalleryWidget
}

const API_BASE_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/backend/api`

const props = defineProps<Props>()
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

const lightboxOpen = ref(false)
const currentImageIndex = ref(0)
const activeCardIndex = ref(0)
const lightboxThumbnailsRef = ref<HTMLElement | null>(null)

const mobileLayoutClass = computed(() => {
  const layout = props.widget.mobileLayout || 'grid'
  return `layout-${layout}`
})

const carouselSpeedSeconds = computed(() => {
  const seconds = Number(props.widget.carouselSpeed)
  if (!Number.isFinite(seconds) || seconds <= 0) return 20
  return Math.min(120, Math.max(3, seconds))
})

const normalizedImageHeight = computed(() => {
  const raw = props.widget.imageHeight
  if (raw === undefined || raw === null || String(raw).trim() === '') return '200px'
  const value = String(raw).trim()
  if (/^\d+(\.\d+)?$/.test(value)) return `${value}px`
  return value
})

const widgetRootStyle = computed(() => {
  const styles: Record<string, string> = { ...widgetStyles.value }
  delete styles.height
  delete styles.minHeight
  delete styles.maxHeight

  const fadeSize = String(props.widget.carouselFadeSize || '10%').trim() || '10%'

  assignResponsiveCssVar(
    styles,
    '--pb-image-height',
    normalizedImageHeight.value,
    props.widget.imageHeightMobile,
  )

  if (props.widget.columnsMobile) {
    styles['--gallery-columns-mobile'] = String(props.widget.columnsMobile)
  }

  return {
    ...styles,
    '--gallery-columns': String(props.widget.columns || 3),
    '--gallery-gap': props.widget.spacing || '1rem',
    '--carousel-fade-size': fadeSize,
  }
})

const galleryStyles = computed(() => {
  const layout = props.widget.mobileLayout || 'grid'
  const base = {
    gap: props.widget.spacing || '1rem',
  }

  if (layout === 'carousel') {
    return base
  }

  return {
    ...base,
    display: 'grid',
  }
})

const itemStyles = computed(() => ({
  cursor: props.widget.lightbox ? 'pointer' : 'default',
}))

const currentImage = computed(() => {
  return props.widget.images[currentImageIndex.value] || { url: '', alt: '', caption: '' }
})

const getImageUrl = (imageUrl: string, image?: any) => {
  if (!imageUrl)
    return `${API_BASE_URL}/placeholder.php?width=400&height=300&text=Gallery Image&bg=e9ecef&color=6c757d`

  // Clean up the path - normalize it
  let cleanPath = imageUrl.trim()

  // If it's already a full URL, use it as-is
  if (cleanPath.startsWith('http')) {
    return cleanPath
  }

  // Remove any leading/trailing slashes for consistency
  cleanPath = cleanPath.replace(/^\/+|\/+$/g, '')

  // If it already has /backend/uploads, return it with leading slash
  if (cleanPath.startsWith('backend/uploads')) {
    return `/${cleanPath}`
  }

  // If it starts with a known upload subfolder (imgs, icons, gallery, etc.), prepend backend/uploads
  if (cleanPath.startsWith('imgs/') || cleanPath.startsWith('icons/') || cleanPath.startsWith('gallery/') || 
      cleanPath.startsWith('products/') || cleanPath.startsWith('posts/') || cleanPath.startsWith('logos/')) {
    return `/backend/uploads/${cleanPath}`
  }

  // For gallery widget images without folder prefix, assume imgs/images/
  // This handles legacy database entries that only have the filename
  if (!cleanPath.includes('/')) {
    return `/backend/uploads/imgs/images/${cleanPath}`
  }

  // If path contains 'images/' but not 'imgs/', prepend 'imgs/'
  if (cleanPath.includes('images/') && !cleanPath.startsWith('imgs/')) {
    return `/backend/uploads/imgs/${cleanPath}`
  }

  // Default to imgs/images/ for gallery widget (since gallery uploads go there)
  return `/backend/uploads/imgs/images/${cleanPath}`
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = `${API_BASE_URL}/placeholder.php?width=400&height=300&text=Image Error&bg=f8d7da&color=721c24`
}

const getIconUrl = (iconPath: string) => {
  if (!iconPath) return ''

  // Clean up the path
  let cleanPath = iconPath.trim()

  // If it's already a full URL, use it as-is
  if (cleanPath.startsWith('http')) {
    return cleanPath
  }

  // Remove leading/trailing slashes for consistency
  cleanPath = cleanPath.replace(/^\/+|\/+$/g, '')

  // If it already has /backend/uploads, return with leading slash
  if (cleanPath.startsWith('backend/uploads')) {
    return `/${cleanPath}`
  }

  // If it starts with known upload subfolder, prepend backend/uploads
  if (cleanPath.startsWith('imgs/') || cleanPath.startsWith('icons/') || cleanPath.startsWith('gallery/') || 
      cleanPath.startsWith('products/') || cleanPath.startsWith('posts/') || cleanPath.startsWith('logos/')) {
    return `/backend/uploads/${cleanPath}`
  }

  // Build the full path (legacy fallback)
  return `/backend/uploads/${cleanPath}`
}

const getStackCardStyle = (index: number) => {
  if (props.widget.mobileLayout !== 'stack-cards') return {}

  const offset = (index - activeCardIndex.value) * 10
  const scale = index === activeCardIndex.value ? 1 : 0.95
  const zIndex = props.widget.images.length - Math.abs(index - activeCardIndex.value)

  return {
    transform: `translateX(${offset}px) scale(${scale})`,
    zIndex: zIndex,
    opacity: Math.abs(index - activeCardIndex.value) > 2 ? 0 : 1,
  }
}

const getImageWrapperStyle = () => {
  const styles: Record<string, string> = {
    flexShrink: '0',
  }

  const radius =
    (props.widget as any).imageBorderRadius ?? (props.widget as any).borderRadius
  if (radius && props.widget.styleMode !== 'cards') {
    const r = String(radius)
    styles.borderRadius = isNaN(Number(r.replace(/px$/, ''))) ? r : `${r.replace(/px$/, '')}px`
  }

  return styles
}

const getCarouselItemStyle = (index: number) => {
  const styles = { ...getImageItemStyle(index) }
  if (props.widget.mobileLayout !== 'carousel') return styles

  const cols = Math.max(1, Number(props.widget.columns) || 3)
  const slideWidth = `calc((100cqw - (${cols} - 1) * var(--gallery-gap, 1rem)) / ${cols})`

  return {
    ...styles,
    flex: `0 0 ${slideWidth}`,
    width: slideWidth,
    minWidth: slideWidth,
    maxWidth: slideWidth,
  }
}

const getImageItemStyle = (index: number) => {
  const styles: Record<string, string> = {}

  if (props.widget.styleMode === 'cards') {
    const radius =
      (props.widget as any).imageBorderRadius ?? (props.widget as any).borderRadius
    if (radius) {
      const r = String(radius)
      styles.borderRadius = isNaN(Number(r.replace(/px$/, ''))) ? r : `${r.replace(/px$/, '')}px`
    }
  }

  return styles
}

const handleImageClick = (index: number) => {
  if (props.widget.mobileLayout === 'stack-cards') {
    activeCardIndex.value = index
    if (props.widget.lightbox) {
      openLightboxWithKeyboard(index)
    }
  } else if (props.widget.lightbox) {
    openLightboxWithKeyboard(index)
  }
}

const handleGalleryItemClick = (image: any, index: number) => {
  if (props.widget.lightbox) {
    handleImageClick(index)
  } else if (image.link) {
    openExternalLink(image.link)
  }
}

const openExternalLink = (link: string) => {
  if (typeof window !== 'undefined') {
    window.open(link, '_blank')
  }
}

const openLightbox = (index: number) => {
  if (!props.widget.lightbox) return

  currentImageIndex.value = index
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightboxOpen.value = false
  document.body.style.overflow = 'auto'
  document.removeEventListener('keydown', handleKeydown)
}

const prevImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--
  }
}

const nextImage = () => {
  if (currentImageIndex.value < props.widget.images.length - 1) {
    currentImageIndex.value++
  }
}

const goToImage = (index: number) => {
  if (index >= 0 && index < props.widget.images.length) {
    currentImageIndex.value = index
  }
}

const scrollActiveThumbnailIntoView = () => {
  nextTick(() => {
    const container = lightboxThumbnailsRef.value
    if (!container) return
    const active = container.querySelector('.lightbox-thumbnail.active') as HTMLElement | null
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  })
}

watch(currentImageIndex, scrollActiveThumbnailIntoView)
watch(lightboxOpen, (open) => {
  if (open) scrollActiveThumbnailIntoView()
})

const handleKeydown = (e: KeyboardEvent) => {
  if (!lightboxOpen.value) return

  switch (e.key) {
    case 'ArrowLeft':
      prevImage()
      break
    case 'ArrowRight':
      nextImage()
      break
    case 'Escape':
      closeLightbox()
      break
  }
}

// Add keyboard listener when lightbox opens
const openLightboxWithKeyboard = (index: number) => {
  openLightbox(index)
  document.addEventListener('keydown', handleKeydown)
}
</script>

<style scoped>
.gallery-widget {
  margin: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* Gallery grid styles are applied via computed styles */

.gallery-item {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: block;
  text-decoration: none;
  color: inherit;
}

/* Animated Border Gradient Glow Effect */
@keyframes gradientGlow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.gallery-item:has(.image-content),
.gallery-item.card-style {
  overflow: visible;
  height: auto;
}

.gallery-item.with-glow {
  position: relative;
  overflow: visible;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.gallery-item.with-glow::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 12px;
  padding: 0;
  background: linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #ff0080);
  background-size: 300% 300%;
  animation: gradientGlow 4s ease infinite;
  z-index: -1;
  pointer-events: none;
}

.gallery-item.with-glow .image-wrapper {
  position: relative;
  z-index: 1;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

.gallery-item:hover {
  transform: scale(1.05) translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

/* Link variant - no default link styling */
.gallery-card-link {
  text-decoration: none;
  color: inherit;
}

.gallery-card-link:visited {
  color: inherit;
}

.gallery-item:hover .gallery-image {
  transform: scale(1.1);
}

.image-wrapper {
  width: 100%;
  height: var(--pb-gallery-image-height, 200px);
  min-height: var(--pb-gallery-image-height, 200px);
  max-height: var(--pb-gallery-image-height, 200px);
  flex-shrink: 0;
  overflow: hidden;
  background: #f0f0f0;
  position: relative;
}

.gallery-image {
  width: 100%;
  height: 100%;
  min-height: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Card style variant */
.card-style {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: visible;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: auto;
}

.card-style .image-wrapper {
  flex: 0 0 auto;
  width: 100%;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

.card-style:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.card-style .image-content {
  padding: 1rem;
  background: white;
  border-top: 1px solid #f0f0f0;
  border-radius: 0 0 12px 12px;
}

.image-content {
  padding: 0;
}

.image-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  word-wrap: break-word;
}

.title-with-icons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
}

.link-icon {
  font-size: 0.85rem;
  color: #007bff;
  opacity: 0.7;
}

.icons-container {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
}

.title-icon {
  height: 20px;
  width: auto;
  max-width: 25px;
  object-fit: contain;
  border-radius: 3px;
}

.image-caption {
  position: relative;
  background: transparent;
  color: #666;
  padding: 0;
  font-size: 0.85rem;
  text-align: left;
  line-height: 1.3;
}

.empty-gallery {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

/* Lightbox Styles */
.lightbox-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem 4.5rem;
  box-sizing: border-box;
}

.lightbox-content {
  position: relative;
  max-width: min(1200px, 100%);
  max-height: 90vh;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10002;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.75);
  border: 2px solid rgba(255, 255, 255, 0.9);
  color: #fff;
  font-size: 1.25rem;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  transition: background 0.2s ease, transform 0.2s ease;
}

.lightbox-close:hover {
  background: rgba(15, 23, 42, 0.95);
  transform: scale(1.05);
}

.lightbox-image {
  max-width: 100%;
  max-height: calc(90vh - 200px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}

.lightbox-thumbnails {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.35rem 0.25rem;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.35) transparent;
  -webkit-overflow-scrolling: touch;
}

.lightbox-thumbnails::-webkit-scrollbar {
  height: 6px;
}

.lightbox-thumbnails::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.35);
  border-radius: 3px;
}

.lightbox-thumbnail {
  flex: 0 0 auto;
  width: 72px;
  height: 54px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  opacity: 0.6;
  transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.lightbox-thumbnail:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.lightbox-thumbnail.active {
  opacity: 1;
  border-color: #fff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.lightbox-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}

.lightbox-caption {
  color: #fff;
  margin-top: 1rem;
  font-size: 1rem;
  background: rgba(0, 0, 0, 0.65);
  padding: 0.65rem 1rem;
  border-radius: 8px;
  max-width: 100%;
}

.lightbox-counter {
  margin-top: 0.75rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.875rem;
}

.lightbox-nav-btn {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10002;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.75);
  border: 2px solid rgba(255, 255, 255, 0.85);
  color: #fff;
  font-size: 1.35rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  transition: background 0.2s ease, transform 0.2s ease;
}

.lightbox-prev {
  left: 1rem;
}

.lightbox-next {
  right: 1rem;
}

.lightbox-nav-btn:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.95);
  transform: translateY(-50%) scale(1.05);
}

.lightbox-nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Responsive Mobile Layouts */
@media (max-width: 768px) {
  /* Horizontal Scroll Layout */
  .mobile-layout-horizontal-scroll .pb-gallery-grid {
    display: flex !important;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 1rem;
    padding: 0.5rem 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  }

  .mobile-layout-horizontal-scroll .pb-gallery-grid::-webkit-scrollbar {
    height: 6px;
  }

  .mobile-layout-horizontal-scroll .pb-gallery-grid::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }

  .mobile-layout-horizontal-scroll .pb-gallery-grid::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
  }

  .mobile-layout-horizontal-scroll .gallery-item {
    flex: 0 0 85%;
    scroll-snap-align: center;
    max-width: 85%;
  }

  .mobile-layout-horizontal-scroll .gallery-image {
    height: 100%;
    min-height: 100%;
    width: 100%;
  }

  /* Stack Cards Layout */
  .mobile-layout-stack-cards .pb-gallery-grid {
    display: block !important;
    position: relative;
    height: 320px;
    margin: 2rem auto;
    max-width: 300px;
  }

  .mobile-layout-stack-cards .gallery-item {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 280px;
    transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    cursor: pointer;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  .mobile-layout-stack-cards .gallery-item:hover {
    transform: translateX(-50%) translateY(-5px) !important;
  }

  .mobile-layout-stack-cards .gallery-image {
    height: 100%;
    min-height: 100%;
    width: 100%;
    pointer-events: none;
  }

  .mobile-layout-grid .gallery-image {
    height: 100%;
    min-height: 100%;
  }
}

@media (max-width: 576px) {
  .mobile-layout-horizontal-scroll .gallery-item {
    flex: 0 0 90%;
    max-width: 90%;
  }
}

@media (max-width: 768px) {
  .lightbox-modal {
    padding: 1rem 3.25rem;
  }

  .lightbox-close {
    top: 0.75rem;
    right: 0.75rem;
    width: 40px;
    height: 40px;
  }

  .lightbox-prev {
    left: 0.5rem;
  }

  .lightbox-next {
    right: 0.5rem;
  }

  .lightbox-nav-btn {
    width: 42px;
    height: 42px;
    font-size: 1.15rem;
  }

  .lightbox-thumbnail {
    width: 60px;
    height: 45px;
  }
}
@keyframes carousel-scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Carousel Layout - Works on all screen sizes */
.mobile-layout-carousel .pb-gallery-grid {
  display: block !important;
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  container-type: inline-size;
  container-name: pb-gallery-carousel;
}

.mobile-layout-carousel.carousel-fade-edges .pb-gallery-grid {
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--carousel-fade-size, 10%),
    #000 calc(100% - var(--carousel-fade-size, 10%)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--carousel-fade-size, 10%),
    #000 calc(100% - var(--carousel-fade-size, 10%)),
    transparent 100%
  );
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

.mobile-layout-carousel .gallery-carousel-track {
  display: flex;
  width: max-content;
  max-width: none !important;
  animation: carousel-scroll linear infinite;
  /* duration: inline animationDuration from widget.carouselSpeed */
}

.mobile-layout-carousel .gallery-carousel-track:hover {
  animation-play-state: paused;
}

.mobile-layout-carousel .gallery-item {
  flex: 0 0 calc((100cqw - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
  width: calc((100cqw - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
  min-width: calc((100cqw - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
  max-width: calc((100cqw - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3)) !important;
  height: auto;
  align-self: center;
  box-sizing: border-box;
}

.mobile-layout-carousel .gallery-item.card-style {
  overflow: visible;
}

.mobile-layout-carousel .gallery-item:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-layout-carousel .gallery-item:hover .gallery-image {
  transform: none;
}

.mobile-layout-carousel .image-wrapper {
  height: var(--pb-gallery-image-height, 200px) !important;
  min-height: var(--pb-gallery-image-height, 200px) !important;
  max-height: var(--pb-gallery-image-height, 200px) !important;
}

.mobile-layout-carousel .gallery-image {
  width: 100% !important;
  height: 100% !important;
  min-height: 100% !important;
  max-height: 100% !important;
  object-fit: cover !important;
}

@supports not (width: 1cqw) {
  .mobile-layout-carousel .gallery-item {
    flex: 0 0 calc((100% - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
    width: calc((100% - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
    min-width: calc((100% - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3));
    max-width: calc((100% - (var(--gallery-columns, 3) - 1) * var(--gallery-gap, 1rem)) / var(--gallery-columns, 3)) !important;
  }
}

/* Carousel Animation */
</style>
